// React
import { type ButtonHTMLAttributes, type Ref } from "react";

// Hooks and utilities
import { cn } from "@/lib/utils";
import { FIELD_SURFACE_BORDERED } from "@/components/ui/field-surface";

export const FIELD_TRIGGER_CLASSES = cn(
  // layout
  "flex w-full items-center justify-between gap-3",
  FIELD_SURFACE_BORDERED,
  // spacing
  "px-6 py-4",
  // A trigger is sized by its content, so one that renders nothing — an unset
  // Select given no placeholder — collapses to its padding (34px, against the
  // 45.59px a filled trigger measures). A floor, deliberately under that natural
  // height: a floor above it would grow every trigger rather than lift the empty
  // ones. Not a pseudo-element — this is a flex row with exactly two children,
  // so a ::before becomes a third item and `justify-between` re-centres the label.
  "min-h-15",
  // typography
  "font-inter text-sm font-normal",
  // Radix marks an unselected Select with data-placeholder; grey it the way
  // DropdownTrigger greys its own placeholder, so the two read alike.
  "data-[placeholder]:text-grey-light",
  // states — border stays dinnerplate-white in every state; no
  // grey/50 hover tint, no brand-yellow focus ring (the canonical
  // Grain pill never paints a darker outline at rest).
  "hover:border-dinnerplate-white",
  "focus:border-dinnerplate-white focus-visible:border-dinnerplate-white",
  "focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0",
  "disabled:border-grey-light disabled:bg-dinnerplate-white-light disabled:text-grey-light disabled:cursor-not-allowed disabled:shadow-none",
  "aria-disabled:border-grey-light aria-disabled:bg-dinnerplate-white-light aria-disabled:cursor-default aria-disabled:shadow-none",
);

// Colour stays out of the constant: it varies per status.
export const STATUS_INDICATOR_CLASSES = cn(
  FIELD_TRIGGER_CLASSES,
  "w-[194px] py-4 font-semibold shadow-soft",
);

export function FieldTrigger({
  className,
  ref,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(FIELD_TRIGGER_CLASSES, className)}
      {...props}
    >
      {children}
    </button>
  );
}
